import { action } from '@ember/object';
import Component from '@glimmer/component';
import currency from 'currency.js';
import { dateToUtc } from 'donor-elf-web/lib/date-utils';
import { type JournalIntervalCumulativeDecisionSummary } from 'donor-elf-web/lib/journal-utils';
import JournalModel from 'donor-elf-web/models/journal';
import JournalGoalModel from 'donor-elf-web/models/journal-goal';
import { pluralize } from 'ember-inflector';
import Highcharts from 'highcharts';
import moment from 'moment';

interface ComponentArgs {
  journal: JournalModel;
  journalGoal: JournalGoalModel;
  // intervalSummaries must only be for the decisions related to the journalGoal
  intervalSummaries: JournalIntervalCumulativeDecisionSummary[];
}

export default class JournalProjectedGoalNewComponent extends Component<ComponentArgs> {
  get hasEnoughData() {
    return this.args.intervalSummaries.length > this.timeFrameWeeks + 1;
  }

  get intervalSummaries() {
    return this.args.intervalSummaries;
  }

  get lastIntervalSummary() {
    return this.args.intervalSummaries[this.args.intervalSummaries.length - 1];
  }

  get goalDate() {
    return this.args.journal.goalDate;
  }

  get weeksUntilGoalDate() {
    let { lastIntervalSummary, goalDate } = this;
    return Math.ceil(
      moment(goalDate).diff(
        moment(lastIntervalSummary.date.end),
        'weeks',
        true,
      ),
    );
  }

  get timeFrameWeeks() {
    return 4;
  }

  get timeFrameIntervalSummaries() {
    let { intervalSummaries, lastIntervalSummary } = this;
    let sliceEnd = intervalSummaries.indexOf(lastIntervalSummary) + 1;
    let startIndex = sliceEnd - this.timeFrameWeeks;
    return intervalSummaries.slice(startIndex, sliceEnd);
  }

  get timeFrameDecisionAmount() {
    return this.timeFrameIntervalSummaries.reduce(
      (sum: number, item: JournalIntervalCumulativeDecisionSummary) => {
        return currency(sum).add(item.newAmount).value;
      },
      0,
    );
  }

  get averageWeeklyNewDecisionAmount() {
    return this.timeFrameDecisionAmount / this.timeFrameWeeks;
  }

  get currentAmount() {
    return this.lastIntervalSummary.cumulativeAmount;
  }

  get amountToRaise() {
    return currency(this.args.journalGoal.amount).subtract(this.currentAmount)
      .value;
  }

  get weeksLeft() {
    return Math.ceil(this.amountToRaise / this.averageWeeklyNewDecisionAmount);
  }

  get hasReachedGoal() {
    return this.currentAmount >= this.args.journalGoal.amount;
  }

  get reachedGoalDate(): Date | undefined {
    if (!this.hasReachedGoal) {
      return undefined;
    }
    let goalAmount = this.args.journalGoal.amount;
    let interval = this.intervalSummaries.find(
      (x) => x.cumulativeAmount >= goalAmount,
    );
    return interval?.date.endDate;
  }

  get reachedGoalByLabel() {
    let diffWeeks = moment(this.reachedGoalDate).diff(
      moment(this.goalDate),
      'weeks',
      true,
    );
    if (diffWeeks > 8) {
      let diffMonths = Math.ceil(
        moment(this.reachedGoalDate).diff(moment(this.goalDate), 'weeks', true),
      );
      return `${diffMonths} months late`;
    } else if (diffWeeks >= 1) {
      return `${pluralize(Math.ceil(diffWeeks), 'week')} late`;
    } else if (diffWeeks <= 1) {
      return `${pluralize(Math.ceil(diffWeeks) * -1, 'week')} early`;
    } else {
      return 'By the goal date';
    }
  }

  get unknownProjectedFundedDate() {
    return !this.hasEnoughData || this.timeFrameDecisionAmount < 1;
  }

  get unknownProjectedFundedDateReason() {
    if (!this.hasEnoughData) {
      return 'Less than 4 weeks of data';
    } else {
      return 'No new decisions in the last 4 weeks';
    }
  }

  get projectedFundedDate() {
    return moment(this.lastIntervalSummary.date.end)
      .add(this.weeksLeft, 'weeks')
      .toDate();
  }

  get projectedFundedDateByLabel() {
    let diffWeeks = moment(this.projectedFundedDate).diff(
      moment(this.goalDate),
      'weeks',
      true,
    );
    if (diffWeeks > 8) {
      let diffMonths = Math.ceil(
        moment(this.projectedFundedDate).diff(
          moment(this.goalDate),
          'weeks',
          true,
        ),
      );
      return `${diffMonths} months late`;
    } else if (diffWeeks >= 1) {
      return `${pluralize(Math.ceil(diffWeeks), 'week')} late`;
    } else if (diffWeeks <= 1) {
      return `${pluralize(Math.ceil(diffWeeks) * -1, 'week')} early`;
    } else {
      return 'By the goal date';
    }
  }

  get projectedFundedDateWeeksAfterGoal() {
    return Math.ceil(
      moment(this.projectedFundedDate).diff(
        moment(this.goalDate),
        'weeks',
        true,
      ),
    );
  }

  get projectedFundedDateMonthsAfterGoal() {
    return Math.ceil(
      moment(this.projectedFundedDate).diff(
        moment(this.goalDate),
        'months',
        true,
      ),
    );
  }

  get projectedFundedDateWeeksBeforeGoal() {
    return this.projectedFundedDateWeeksAfterGoal * -1;
  }

  @action
  renderChart(element: any) {
    Highcharts.chart(element, this.getChartSettings());
  }

  get journalGoalDateUtc() {
    return dateToUtc(this.goalDate);
  }

  get seriesStartUtc() {
    let firstIntervalStartDate = this.intervalSummaries[0].date.startDate;
    let date = moment(firstIntervalStartDate).add({ days: -1 }).toDate();
    return dateToUtc(date);
  }

  get decisionsSeries() {
    let {
      seriesStartUtc,
      args: {
        intervalSummaries,
        journal: { decisionsRequireGift },
      },
    } = this;

    return {
      name: 'All Decisions',
      type: decisionsRequireGift ? 'line' : 'area',
      color: '#8ac7ec',
      marker: {
        enabled: false,
      },
      data: [[seriesStartUtc, 0]].concat(
        intervalSummaries.map((item) => {
          return [dateToUtc(item.date.endDate), item.cumulativeAmount];
        }),
      ),
    };
  }

  get withGiftSeries() {
    let {
      seriesStartUtc,
      args: { intervalSummaries },
    } = this;

    return {
      name: 'Decisions with Gifts',
      type: 'area',
      color: '#0b68a1',
      marker: {
        enabled: false,
      },
      data: [[seriesStartUtc, 0]].concat(
        intervalSummaries.map((item) => {
          return [dateToUtc(item.date.endDate), item.cumulativeWithGiftAmount];
        }),
      ),
    };
  }

  get goalSeries() {
    let {
      seriesStartUtc,
      args: { journalGoal },
    } = this;
    let maxDate = this.args.journal.goalDate;
    if (this.projectedFundedDate > maxDate) {
      maxDate = this.projectedFundedDate;
    }

    return {
      name: 'Goal',
      type: 'line',
      color: '#f0ad4e',
      lineWidth: 2,
      marker: {
        enabled: false,
      },
      data: [
        [seriesStartUtc, journalGoal.amount],
        [dateToUtc(maxDate), journalGoal.amount],
      ],
    };
  }

  get projectedSeries() {
    let lastIntervalUtc = dateToUtc(this.lastIntervalSummary.date.endDate);
    let projectedFundedDateUtc = dateToUtc(this.projectedFundedDate);
    return {
      name: 'Projected',
      type: 'line',
      color: '#eceeef',
      marker: {
        enabled: false,
      },
      data: [
        [lastIntervalUtc, this.currentAmount],
        [projectedFundedDateUtc, this.args.journalGoal.amount],
      ],
    };
  }

  get goalDateColumnSeries() {
    let {
      journalGoalDateUtc,
      args: { journalGoal },
    } = this;

    return {
      name: 'Goal Date',
      type: 'column',
      color: '#f0ad4e',
      pointWidth: 2,
      borderWidth: 0,
      marker: {
        enabled: false,
      },
      data: [[journalGoalDateUtc, journalGoal.amount]],
    };
  }

  getChartSettings() {
    let series = [];
    series.push(this.decisionsSeries);
    series.push(this.withGiftSeries);
    series.push(this.goalDateColumnSeries);
    if (!this.hasReachedGoal) {
      series.push(this.projectedSeries);
    }
    series.push(this.goalSeries);

    return {
      chart: {
        height: 200,
        margin: [2, 0, 2, 0],
        borderWidth: 0,
      },
      title: {
        text: undefined,
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          // don't display the dummy year
          month: '%e. %b',
          year: '%b',
        },
        startOnTick: false,
      },
      yAxis: {
        min: 0,
        labels: {
          enabled: false,
        },
        max: Math.max(this.args.journalGoal.amount, this.currentAmount) * 1.05,
        title: {
          text: null,
        },
        startOnTick: false,
        endOnTick: false,
        tickPositions: [0],
      },
      legend: {
        enabled: false,
      },
      series,
    } as Highcharts.Options;
  }
}
